import { useState } from 'react';
import { hotComments } from '@/mocks/homeData';

const AVATAR_TONES = [
  'bg-primary-100 text-primary-700',
  'bg-accent-100 text-accent-800',
  'bg-secondary-100 text-secondary-800',
  'bg-background-200 text-foreground-800',
];

export default function HotCommentsSection() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [sort, setSort] = useState<'hot' | 'new'>('hot');

  const toggleLike = (index: number) => {
    setLiked((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <section id="comments" className="w-full border-t border-background-200 bg-background-50 py-10 md:py-14">
      <div className="mx-auto w-full max-w-[1360px] px-4 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-secondary-600">
              Discussion
            </span>
            <h2 className="mt-2 font-heading text-xl font-black tracking-tight text-foreground-950 md:text-3xl">
              评论区才是第二现场
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              很多线索不是在正文里，而是在楼层里被补上的。黑料六点今日六点的热评区按互动量排序，先看大家在追问什么。
            </p>
          </div>
          <div className="flex items-center gap-1 self-start rounded-full border border-background-200 bg-background-100 p-1">
            {[
              { key: 'hot' as const, label: '最热' },
              { key: 'new' as const, label: '最新' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSort(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                  sort === tab.key
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px] lg:gap-10">
          <ul className="space-y-3">
            {hotComments.map((comment, index) => (
              <li
                key={comment.floor}
                className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-black ${
                      AVATAR_TONES[index % AVATAR_TONES.length]
                    }`}
                  >
                    {comment.user.slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-950">{comment.user}</span>
                      <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[10px] font-semibold text-secondary-800">
                        {comment.badge}
                      </span>
                      <span className="text-[11px] text-foreground-500">
                        {comment.floor} · {comment.time}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-800">
                      {comment.content}
                    </p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                      <button
                        type="button"
                        onClick={() => toggleLike(index)}
                        className={`flex cursor-pointer items-center gap-1.5 transition-colors ${
                          liked[index] ? 'text-primary-600' : 'hover:text-primary-600'
                        }`}
                      >
                        <i className={`${liked[index] ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm`} />
                        {comment.likes + (liked[index] ? 1 : 0)}
                      </button>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-chat-3-line text-sm" />
                        {comment.replies} 回复
                      </span>
                      <span className="ml-auto flex items-center gap-1.5">
                        <i className="ri-share-forward-line text-sm" />
                        转发
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="font-heading text-sm font-black text-foreground-950">我也说两句</p>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                看到线索就丢上来，楼层会按互动量自动往前排。
              </p>
              <textarea
                rows={4}
                maxLength={500}
                placeholder="补充你手上的截图、时间点或者那段原片…"
                className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-foreground-500">最多 500 字</span>
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line" />
                  发布
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="font-heading text-lg font-black text-foreground-950">8,412</p>
                <p className="mt-1 text-xs text-foreground-600">今日评论</p>
              </div>
              <div className="rounded-lg border border-accent-300 bg-accent-50 p-4">
                <p className="font-heading text-lg font-black text-accent-800">642</p>
                <p className="mt-1 text-xs text-foreground-600">新增线索</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}